import { useEffect, useRef, useState } from 'react';

import { Alert, Button, WinnerTicket } from '@/components';
import { useWhitelabelConfig } from '@/hooks/useWhitelabelConfig';
import { ValidationKey } from '@/types/enums';
import {
  getInAppBrowserInfo,
  inAppBrowserDownloadNotice,
  parseBoolean,
  ticketLabels,
  type InAppBrowserInfo,
} from '@/utils';

import { useCartRaffleAccordion } from '../TicketsContent/CartRaffleAccordion/hooks/useCartRaffleAccordion';

type Ticket = {
  id: string;
  sequence: string;
};

const NOT_IN_APP: InAppBrowserInfo = { isInAppBrowser: false, platform: 'other' };

export const DownloadTicket = ({ ticket }: { ticket?: Ticket }) => {
  const { handleDownloadTicketWithFallback } = useCartRaffleAccordion();
  const { getValue } = useWhitelabelConfig();
  const [isDownloading, setIsDownloading] = useState(false);
  const [inAppBrowser, setInAppBrowser] = useState<InAppBrowserInfo>(NOT_IN_APP);
  const downloadInFlightRef = useRef(false);

  useEffect(() => {
    setInAppBrowser(getInAppBrowserInfo());
  }, []);

  const otpRequired = parseBoolean(
    getValue<boolean>(ValidationKey.VALIDATION_OTP_REQUIRED_DOWNLOAD_RECEIPT),
    false,
  );

  const handleDownloadTicket = async () => {
    const ticketId = ticket?.id || '';

    if (!ticketId || downloadInFlightRef.current) return;

    downloadInFlightRef.current = true;
    setIsDownloading(true);

    try {
      await handleDownloadTicketWithFallback(ticketId);
    } catch {
      // The download hook is responsible for user-facing error feedback.
    } finally {
      downloadInFlightRef.current = false;
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 p-4">
      <h1 className="w-full text-center text-lg font-bold">
        Fazer Download Ficha cadastral e {ticketLabels.singularCaps}
      </h1>
      <WinnerTicket className="flex-[0_0_45%] w-full" value={ticket?.sequence || ''} />

      {inAppBrowser.isInAppBrowser && (
        <Alert
          data-cy="download-ticket-in-app-hint"
          variant="info"
          description={inAppBrowserDownloadNotice(inAppBrowser.platform, {
            otpRequired,
          })}
        />
      )}

      <div>
        <Button
          className="w-full"
          variant="secondary"
          onClick={handleDownloadTicket}
          loading={isDownloading}
          disabled={isDownloading}
          loadingText={inAppBrowser.isInAppBrowser ? 'Abrindo...' : 'Baixando...'}
        >
          {inAppBrowser.isInAppBrowser ? 'Abrir Comprovante' : 'Download Comprovante'}
        </Button>
      </div>
    </div>
  );
};
